import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const About = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="About Seoro - AI-Powered SEO Content Automation Platform"
        description="Learn about Seoro's mission to automate SEO content creation and backlink building. We help businesses grow organic traffic automatically through AI-powered content generation, premium backlinks, and real performance tracking."
        keywords="about Seoro, SEO automation company, content marketing platform, AI SEO tools, organic traffic growth, automated content creation"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              About Seoro
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to dominate search rankings—automated content creation, premium backlinks, and real performance tracking.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Our Mission</h2>
              <p className="text-muted-foreground leading-relaxed">
                We believe growing organic traffic shouldn't require a team of SEO experts or hours of manual work. 
                Our mission is simple: automate everything that matters—from content creation to backlink building—so 
                you can focus on what actually grows your business.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">What We Do</h2>
              <p className="text-muted-foreground leading-relaxed">
                Seoro is your all-in-one SEO content platform. We analyze your niche, generate 30 SEO-optimized 
                articles monthly with AI-generated images, deliver 10 premium backlinks (DA 70+) every month, and 
                track your real performance with Google Search Console. Everything runs automatically—you just 
                review and publish.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Why Choose Seoro</h2>
              <ul className="list-disc list-inside space-y-3 text-muted-foreground">
                <li><strong className="text-foreground">AI content that actually ranks</strong>—Our AI writes naturally, not like a robot. Every article is optimized for search engines and real readers.</li>
                <li><strong className="text-foreground">Premium backlinks delivered monthly</strong>—Get 10 high-authority backlinks (DA 70+) automatically. No outreach, no negotiation, just results.</li>
                <li><strong className="text-foreground">Real performance data</strong>—Connect Google Search Console to see actual clicks, impressions, and rankings. Make decisions based on real data.</li>
                <li><strong className="text-foreground">Publish anywhere instantly</strong>—One-click publishing to Shopify, WordPress, and Webflow. Framer and more integrations coming soon.</li>
                <li><strong className="text-foreground">76 languages supported</strong>—Create content in your audience's language. Our AI writes naturally in any language, not translations.</li>
                <li><strong className="text-foreground">AI-generated images included</strong>—Every article comes with high-quality images in multiple styles, automatically inserted throughout your content.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Join Thousands of Growing Businesses</h2>
              <p className="text-muted-foreground leading-relaxed">
                Entrepreneurs and marketers trust Seoro to handle their SEO content strategy. Stop struggling with 
                content creation and backlink building. Start ranking higher and growing your organic traffic—all 
                while you focus on what matters most.
              </p>
              <Button
                onClick={handleSignIn}
                className="rounded-full px-8 py-6 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                <span>Get Started Now</span>
                <ArrowRight className="h-5 w-5" />
              </Button>
            </section>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default About;


